import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';

const STORAGE_KEY = 'sg_preferred_role';

/** Maps a role to its dashboard base path. */
function roleToDashboard(role: UserRole): string {
  switch (role) {
    case 'property_manager':
    case 'security_supervisor':
      return '/manager';
    case 'guard':
      return '/guard';
    case 'enterprise_tenant_admin':
      return '/enterprise';
    case 'employee':
      return '/employee';
    case 'org_admin':
      return '/org';
    case 'resident':
    default:
      return '/resident';
  }
}

export interface SwitchableRole {
  role: UserRole;
  dashboard: string;
}

/**
 * Hook that exposes the list of roles the current user can switch to
 * (i.e. roles other than the one they're currently viewing) and a
 * function to perform the switch.
 *
 * @param currentRole - The role surface the user is currently on.
 */
export function useRoleSwitcher(currentRole: UserRole) {
  const { user } = useAuth();
  const navigate = useNavigate();

  /** Distinct roles the user holds, excluding the current one. */
  const switchableRoles = useMemo<SwitchableRole[]>(() => {
    if (!user) return [];
    const seen = new Set<UserRole>();
    const roles: SwitchableRole[] = [];
    for (const pr of user.propertyRoles) {
      if (pr.role === currentRole) continue;
      if (seen.has(pr.role)) continue;
      seen.add(pr.role);
      roles.push({ role: pr.role, dashboard: roleToDashboard(pr.role) });
    }
    return roles;
  }, [user, currentRole]);

  /** Navigate to another role's dashboard and persist the preference. */
  const switchTo = useCallback(
    (target: SwitchableRole) => {
      try {
        localStorage.setItem(STORAGE_KEY, target.role);
      } catch {
        // localStorage unavailable — non-critical
      }
      void navigate(target.dashboard);
    },
    [navigate],
  );

  return { switchableRoles, switchTo, hasMultipleRoles: switchableRoles.length > 0 };
}

/**
 * Reads the user's last-used role preference from localStorage.
 * Returns null if none is stored or localStorage is unavailable.
 */
export function getPreferredRole(): UserRole | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored as UserRole;
  } catch {
    // localStorage unavailable
  }
  return null;
}
